type StripeErrorLike = {
    type?: unknown;
    code?: unknown;
    decline_code?: unknown;
    message?: unknown;
    response?: unknown;
    status?: unknown;
    data?: unknown;
};

export type PaymentErrorAction = 'sign_in' | 'manage_subscription' | 'choose_plan' | 'restart_checkout';

export type PaymentErrorDetails = {
    message: string;
    action?: PaymentErrorAction;
};

const GENERIC_DECLINE_MESSAGE = 'Your payment was declined. Try another payment method or contact your bank.';
const TEMPORARY_FAILURE_MESSAGE = 'We could not process the payment right now. Please try again.';
const AUTHENTICATION_MESSAGE = 'Payment authentication was not completed. Please try again or use another payment method.';
const NETWORK_FAILURE_MESSAGE = 'We could not reach the payment service. Check your connection and try again.';
const nativeFetchNetworkMessages = new Set([
    'Failed to fetch',
    'Load failed',
    'NetworkError when attempting to fetch resource.'
]);

const toOptionalString = (value: unknown): string | null =>
    typeof value === 'string' && value.trim().length > 0 ? value : null;

const asStripeError = (error: unknown): StripeErrorLike | null =>
    typeof error === 'object' && error !== null ? error as StripeErrorLike : null;

const toHttpStatus = (value: unknown): number | null =>
    typeof value === 'number' && Number.isFinite(value) ? value : null;

const asRecord = (value: unknown): Record<string, unknown> | null =>
    typeof value === 'object' && value !== null ? value as Record<string, unknown> : null;

/** Accept both Axios-style response envelopes and the lightweight RestAPI error shape. */
const getErrorHttpStatus = (error: StripeErrorLike | null): number | null => {
    const response = asRecord(error?.response);
    return toHttpStatus(response?.status) ?? toHttpStatus(error?.status);
};

const getErrorResponseData = (error: StripeErrorLike | null): unknown => {
    const response = asRecord(error?.response);
    return response?.data ?? error?.data ?? error?.response;
};

const isNativeFetchNetworkFailure = (error: unknown): error is TypeError => (
    error instanceof TypeError && nativeFetchNetworkMessages.has(error.message)
);

const getValidationMessages = (value: unknown): string[] => {
    if (Array.isArray(value)) {
        return value.flatMap(getValidationMessages);
    }
    if (typeof value === 'object' && value !== null) {
        return Object.values(value).flatMap(getValidationMessages);
    }
    const message = toOptionalString(value);
    return message ? [message] : [];
};

const getResponseMessage = (value: unknown): string | null => {
    if (typeof value === 'string') {
        return toOptionalString(value);
    }
    if (typeof value !== 'object' || value === null) {
        return null;
    }

    const response = value as {
        detail?: unknown;
        Detail?: unknown;
        error?: unknown;
        Error?: unknown;
        errors?: unknown;
        Errors?: unknown;
        message?: unknown;
        Message?: unknown;
        title?: unknown;
        Title?: unknown;
    };
    return toOptionalString(response.detail)
        ?? toOptionalString(response.Detail)
        ?? toOptionalString(response.message)
        ?? toOptionalString(response.Message)
        ?? toOptionalString(response.error)
        ?? toOptionalString(response.Error)
        ?? toOptionalString(getValidationMessages(response.errors ?? response.Errors).join(' '))
        ?? toOptionalString(response.title)
        ?? toOptionalString(response.Title);
};

const getResponseCode = (value: unknown): string | null => {
    if (typeof value !== 'object' || value === null) {
        return null;
    }

    const response = value as { code?: unknown; Code?: unknown };
    return toOptionalString(response.code) ?? toOptionalString(response.Code);
};

const declineMessage = (declineCode: string | null): string => {
    switch (declineCode) {
        case 'authentication_required':
        case 'authentication_not_handled':
            return AUTHENTICATION_MESSAGE;
        case 'expired_card':
            return 'Your card has expired. Please use another card.';
        case 'incorrect_cvc':
        case 'invalid_cvc':
            return 'Your card security code is incorrect. Check it and try again.';
        case 'incorrect_number':
        case 'invalid_number':
            return 'Your card number is incorrect. Check it and try again.';
        case 'incorrect_address':
        case 'incorrect_zip':
            return 'Your billing address is incorrect. Check it and try again.';
        case 'insufficient_funds':
            return 'Your card has insufficient funds. Please use another payment method.';
        case 'issuer_not_available':
        case 'processing_error':
        case 'reenter_transaction':
        case 'try_again_later':
            return TEMPORARY_FAILURE_MESSAGE;
        default:
            // Fraud, lost/stolen card, Radar blocks, and unknown issuer declines must remain vague.
            return GENERIC_DECLINE_MESSAGE;
    }
};

/**
 * Converts Stripe/browser failures into safe, actionable checkout copy.
 */
export const getPaymentErrorDetails = (error: unknown): PaymentErrorDetails => {
    const stripeError = asStripeError(error);
    const type = toOptionalString(stripeError?.type);
    const code = toOptionalString(stripeError?.code);
    const declineCode = toOptionalString(stripeError?.decline_code);
    const errorMessage = toOptionalString(stripeError?.message);
    const httpStatus = getErrorHttpStatus(stripeError);
    const responseData = getErrorResponseData(stripeError);
    const responseMessage = getResponseMessage(responseData);
    const responseCode = getResponseCode(responseData);
    const reportedMessage = responseMessage ?? errorMessage;

    if (httpStatus === 401 || httpStatus === 403) {
        return {
            message: reportedMessage ?? 'Your sign-in has expired. Sign in again, then retry your payment.',
            action: 'sign_in'
        };
    }

    if (httpStatus === 409 && (
        responseCode === 'checkout_restart_required'
        || responseMessage?.toLowerCase().includes('expired')
    )) {
        return {
            message: responseMessage ?? 'This checkout attempt can no longer be retried. Please try again.',
            action: 'restart_checkout'
        };
    }

    if (httpStatus === 409 && responseCode === 'checkout_already_in_progress') {
        return {
            message: responseMessage
                ?? 'Another checkout is already in progress for this account. Continue with that checkout or try again later.'
        };
    }

    if (httpStatus === 409) {
        return {
            message: reportedMessage ?? 'A subscription already exists and needs attention before another can be created.',
            action: 'manage_subscription'
        };
    }

    if (httpStatus === 400) {
        return {
            message: reportedMessage ?? 'We could not start checkout for this subscription plan. Choose a current plan and try again.',
            action: 'choose_plan'
        };
    }

    if (httpStatus != null && httpStatus >= 500) {
        return { message: reportedMessage ?? TEMPORARY_FAILURE_MESSAGE };
    }

    if (error instanceof Error && error.message === 'User not logged in.') {
        return {
            message: error.message,
            action: 'sign_in'
        };
    }

    if (isNativeFetchNetworkFailure(error)) {
        return { message: NETWORK_FAILURE_MESSAGE };
    }

    if (code === 'ERR_NETWORK' || code === 'ECONNABORTED' || code === 'ETIMEDOUT') {
        return { message: NETWORK_FAILURE_MESSAGE };
    }

    // Stripe documents card and validation error messages as customer-facing.
    // Prefer the exact reported error over deriving less-specific copy from its codes.
    if (errorMessage) {
        return { message: errorMessage };
    }

    if (declineCode) {
        return { message: declineMessage(declineCode) };
    }

    switch (code) {
        case 'payment_intent_authentication_failure':
        case 'setup_intent_authentication_failure':
        case 'authentication_required':
            return { message: AUTHENTICATION_MESSAGE };
        case 'card_declined':
            return { message: GENERIC_DECLINE_MESSAGE };
        case 'expired_card':
        case 'invalid_expiry_month':
        case 'invalid_expiry_year':
            return { message: 'Your card expiry date is invalid. Check it and try again.' };
        case 'incorrect_cvc':
        case 'invalid_cvc':
        case 'incomplete_cvc':
            return { message: 'Your card security code is incorrect. Check it and try again.' };
        case 'incorrect_number':
        case 'invalid_number':
        case 'incomplete_number':
            return { message: 'Your card number is incorrect. Check it and try again.' };
        case 'processing_error':
            return { message: TEMPORARY_FAILURE_MESSAGE };
    }

    if (type === 'validation_error') {
        return { message: 'Check your payment details and try again.' };
    }

    if (type === 'api_connection_error' || type === 'api_error' || type === 'rate_limit_error') {
        return { message: TEMPORARY_FAILURE_MESSAGE };
    }

    return { message: 'We could not complete your payment. Please try again.' };
};

export const getPaymentErrorMessage = (error: unknown): string => getPaymentErrorDetails(error).message;
